import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import Ticker from "@/components/Ticker";
import { PostCard } from "@/components/PostCard";
import MiniPollChart from "@/components/MiniPollChart";
import { renderInlineLinks } from "@/lib/inline";
import { useSettings } from "@/hooks/useSettings";
import SEO, { makeMetaDescription, SITE_ORIGIN, SITE_NAME } from "@/components/SEO";


export default function HomePage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const leadColRef = useRef(null);
  const [sidebarMaxH, setSidebarMaxH] = useState(null);

  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  // Cap the Leaders sidebar height to the lead column so it scrolls instead of
  // stretching the row and leaving slack space.
  useEffect(() => {
    const update = () => {
      if (leadColRef.current && window.innerWidth >= 1024) {
        setSidebarMaxH(leadColRef.current.offsetHeight);
      } else {
        setSidebarMaxH(null);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (leadColRef.current) ro.observe(leadColRef.current);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [posts]);

  const essays = posts.filter((p) => p.type === "essay");
  const forums = posts.filter((p) => p.type === "forum");
  const polls = posts
    .filter((p) => p.type === "poll")
    .slice()
    .sort((a, b) => {
      // Ordered polls first (by explicit poll_order asc), then unordered polls by created_at desc.
      const ao = typeof a.poll_order === "number" ? a.poll_order : null;
      const bo = typeof b.poll_order === "number" ? b.poll_order : null;
      if (ao !== null && bo !== null) return ao - bo;
      if (ao !== null) return -1;
      if (bo !== null) return 1;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  const lead = essays[0] || posts[0];
  const pinned = posts
    .filter((p) => p.pinned && p.id !== lead?.id)
    .sort((a, b) => (b.pinned_at || "").localeCompare(a.pinned_at || ""))
    .slice(0, 3);
  const pinnedIds = new Set(pinned.map((p) => p.id));
  const rest = posts.filter((p) => p.id !== lead?.id && !pinnedIds.has(p.id) && p.type !== "poll");
  const secondary = rest.slice(0, 6);

  const CurrentPollsPanel = ({ className = "", hideHeadingRule = false }) => (
    <aside className={className} data-testid="latest-polls-sidebar">
      <div className={`flex items-baseline justify-between ${hideHeadingRule ? "pb-1 mb-2" : "border-b border-black pb-2 mb-4"}`}>
        <h3 className="serif text-2xl">Current Polls</h3>
        <Link to="/polls" data-testid="latest-polls-all" className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-600 hover:text-amber-700">
          All &rarr;
        </Link>
      </div>
      {loading && <p className="mono text-xs text-neutral-500">Loading&hellip;</p>}
      {!loading && polls.length === 0 && (
        <p className="mono text-xs text-neutral-500">
          No polls yet. <Link to="/submit" className="text-[#fc4707] hover:brightness-90 transition-colors">Start one.</Link>
        </p>
      )}
      {!loading && polls.length > 0 && (
        <ol
          className="space-y-4 lg:overflow-y-auto lg:pr-2 lg:max-h-[520px] latest-polls-scroll"
          data-testid="latest-polls-list"
        >
          {polls.map((p, idx) => (
            <li key={p.id} className="border-b border-black pb-3 last:border-b-0">
              <Link
                to={`/post/${p.id}`}
                data-testid={`latest-poll-link-${idx}`}
                className="group block"
              >
                <div className="mono text-[10px] uppercase tracking-[0.25em] text-amber-700 mb-1">
                  Poll &middot; {p.topic || "General"}
                </div>
                <div className="serif text-lg leading-snug group-hover:text-amber-800 transition-colors">
                  {p.title}
                </div>
                {p.summary && (
                  <p className="text-sm text-neutral-600 mt-1 line-clamp-2">{p.summary}</p>
                )}
                <MiniPollChart post={p} />
                <div className="mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1 tabular-nums">
                  {p.likert_aggregates
                    ? `${Math.max(0, ...p.likert_aggregates.map((r) => r.total || 0))} respondents`
                    : `${p.poll_total_votes || 0} vote${(p.poll_total_votes || 0) === 1 ? "" : "s"}`}
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </aside>
  );

  const homeStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    description: makeMetaDescription(settings.hero_copy),
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_ORIGIN}/editorials?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Layout>
      <SEO
        path="/"
        title={(settings.hero_title || "").replace(/\s*\n+\s*/g, " ")}
        description={makeMetaDescription(settings.hero_copy)}
        structuredData={homeStructuredData}
      />
      {/* Ticker */}
      <Ticker />

      {/* Hero — sits directly on the site background alongside the sidebar so
          both surface the editor-chosen background. Only the masthead / nav /
          ticker at the very top stay opaque. */}
      <section>
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-6 sm:pt-8 pb-14 sm:pb-20 border-b border-black">
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-8 lg:gap-12">
            <div className={`lg:col-span-7 ${(settings.hero_align || "left") === "center" ? "text-center" : ""}`}>
              <h1 className="serif text-5xl sm:text-6xl tracking-tight">
                {(settings.hero_title || "").split("\n").map((line, i, arr) => (
                  <span key={`t-${i}`}>
                    {line}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
                {settings.hero_subtitle && (
                  <>
                    <br />
                    <span className="italic">{settings.hero_subtitle}</span>
                  </>
                )}
              </h1>
              {(() => {
                const isCentered = (settings.hero_align || "left") === "center";
                const [first, ...rest] = (settings.hero_copy || "").split("\n");
                const body = rest.join("\n").trim();
                const paraCls = isCentered
                  ? "mt-6 text-lg text-neutral-900 leading-relaxed max-w-2xl mx-auto"
                  : "mt-6 text-lg text-neutral-900 leading-relaxed max-w-2xl";
                const bodyCls = isCentered
                  ? "mt-3 text-lg text-neutral-700 leading-relaxed max-w-2xl mx-auto whitespace-pre-line"
                  : "mt-3 text-lg text-neutral-700 leading-relaxed max-w-2xl whitespace-pre-line";
                const ruleCls = isCentered ? "mt-3 border-black max-w-2xl mx-auto" : "mt-3 border-black max-w-2xl";
                return (
                  <>
                    <p className={paraCls}>{renderInlineLinks(first)}</p>
                    {body && <hr className={ruleCls} />}
                    {body && (
                      <p className={bodyCls}>
                        {renderInlineLinks(body)}
                      </p>
                    )}
                  </>
                );
              })()}
              <div className={`mt-8 flex flex-wrap gap-3 ${(settings.hero_align || "left") === "center" ? "justify-center" : ""}`}>
                <Link to="/submit" data-testid="hero-submit-link" style={{ backgroundColor: "#fc4707" }} className="text-white px-6 py-3 mono text-xs uppercase tracking-[0.2em] hover:brightness-90 transition-all">
                  Submit content
                </Link>
                <Link to="/debates" data-testid="hero-forums-link" className="border border-black px-6 py-3 mono text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors">
                  Join a debate
                </Link>
              </div>
            </div>

            {/* Current Polls sidebar — desktop only. Mobile version renders after Leaders below. */}
            <CurrentPollsPanel className="hidden lg:block lg:col-span-3 lg:border-l lg:border-black lg:pl-8" />
          </div>
          </div>
        </div>
      </section>

      {/* Front Page */}
      <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4 lg:pb-6">
        <div className="mb-8 border-b border-black pb-3">
          <h3 className="serif text-5xl sm:text-6xl tracking-tight">Leaders</h3>
          <p className="text-neutral-700 mt-3 max-w-2xl">Latest published</p>
        </div>

        {loading && <p className="mono text-sm text-neutral-500">Loading&hellip;</p>}

        {!loading && posts.length === 0 && (
          <div className="border border-dashed border-neutral-400 p-12 text-center">
            <h4 className="serif text-2xl">No stories yet.</h4>
            <p className="text-neutral-600 mt-2">Be the first citizen to submit an essay, forum, or poll.</p>
            <Link to="/submit" data-testid="empty-submit-link" style={{ backgroundColor: "#fc4707" }} className="inline-block mt-6 text-white px-6 py-3 mono text-xs uppercase tracking-[0.2em] hover:brightness-90 transition-all">
              Submit content
            </Link>
          </div>
        )}

        {!loading && posts.length > 0 && (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-x-8 lg:gap-x-12 gap-y-8 items-start">
              {/* Lead story + pinned */}
              {lead && (
                <div ref={leadColRef} className="lg:col-span-7 lg:pr-8">
                  <PostCard post={lead} />
                  {pinned.length > 0 && (
                    <div className="mt-8" data-testid="pinned-under-lead">
                      <div className="grid grid-cols-1 gap-y-8">
                        {pinned.map((p) => (
                          <PostCard key={p.id} post={p} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Sidebar list */}
              <div
                className="lg:col-span-3 space-y-6 lg:border-l lg:border-black lg:pl-8 lg:overflow-y-auto lg:pr-2 latest-polls-scroll"
                style={sidebarMaxH ? { maxHeight: `${sidebarMaxH}px` } : undefined}
              >
                {secondary.slice(0, 3).map((p) => (
                  <PostCard key={p.id} post={p} />
                ))}
              </div>
            </div>
            {/* Below-fold grid — outside the 2-column parent so it flows immediately after both columns end */}
            {(secondary.slice(3).length > 0 || polls[0]) && (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8 pt-8 border-t border-black [&>a:nth-last-child(-n+3)]:border-b-0 [&>a:nth-last-child(-n+3)]:pb-0">
                {secondary.slice(3).map((p) => (
                  <PostCard key={p.id} post={p} />
                ))}
                {/* Featured poll — fills the third column with a mini chart.
                    Uses the LAST poll in the ordered list (most likely to be
                    below the sidebar scroll fold), so it surfaces something
                    that would otherwise be hidden. */}
                {(() => {
                  const belowFoldCount = secondary.slice(3).length;
                  if (belowFoldCount >= 3) return null;
                  const featuredPoll = polls.length > 0 ? polls[polls.length - 1] : null;
                  if (!featuredPoll) return null;
                  return (
                    <Link
                      key={`featured-poll-${featuredPoll.id}`}
                      to={`/post/${featuredPoll.id}`}
                      data-testid="below-fold-featured-poll"
                      className="block group border-black border-b pb-6 last:border-b-0 last:pb-0 transition-colors"
                    >
                      <div className="mono text-[10px] uppercase tracking-[0.25em] text-neutral-900 mb-2">
                        Poll / {new Date(featuredPoll.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }).toUpperCase()}
                      </div>
                      <h3 className="serif text-2xl sm:text-3xl font-medium tracking-tight leading-tight mt-3 group-hover:text-[#fc4707] transition-colors whitespace-pre-line">
                        {featuredPoll.title}
                      </h3>
                      {featuredPoll.summary && (
                        <p className="text-neutral-700 mt-2 line-clamp-3">{featuredPoll.summary}</p>
                      )}
                      <MiniPollChart post={featuredPoll} />
                    </Link>
                  );
                })()}
              </div>
            )}
          </>
        )}
      </section>

      {/* Mobile-only Current Polls (after Leaders) */}
      <section className="lg:hidden max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <CurrentPollsPanel hideHeadingRule />
      </section>
    </Layout>
  );
}
